import type { SupabaseClient } from "@supabase/supabase-js";

// Publish + cancel write contracts for the Schedule Builder, adopted verbatim from Codex's
// hosted RPCs (schedule_template_publish + schedule_slot_cancel migrations). Same posture
// as the assign path: exact p_ named params, EXACT-match allowlist of the contract's
// value-free failure sentences (operator-actionable, rendered verbatim), everything else
// fails closed to one SAFE generic. Receipts parsed defensively - empty means failure.

export const SAFE_PUBLISH_FAILURE = "Couldn't publish that template — please try again.";

const KNOWN_PUBLISH_FAILURES: ReadonlySet<string> = new Set([
  "Publish window start and end dates are required.",
  "Publish window end must be on or after the start date.",
  "Publish window cannot exceed 63 calendar days.",
  "Schedule manage permission is required to publish schedule templates.",
  "Shift template was not found for this account.",
  "Retired shift templates cannot be published.",
]);

export type PublishOutcome =
  | {
      ok: true;
      createdSlotCount: number;
      existingSlotCount: number;
      totalSlotCount: number;
      templateStatus: string;
    }
  | { ok: false; reason: string };

/** Publish a template's slots across a date window (idempotent server-side). Never throws. */
export async function publishShiftTemplate(
  client: SupabaseClient,
  accountId: string,
  shiftTemplateId: string,
  windowStart: string,
  windowEnd: string,
): Promise<PublishOutcome> {
  try {
    const { data, error } = await client.rpc("publish_shift_template", {
      p_account_id: accountId,
      p_shift_template_id: shiftTemplateId,
      p_window_start: windowStart,
      p_window_end: windowEnd,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return { ok: false, reason: KNOWN_PUBLISH_FAILURES.has(message) ? message : SAFE_PUBLISH_FAILURE };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    if (row === undefined || typeof row.total_slot_count !== "number") {
      return { ok: false, reason: SAFE_PUBLISH_FAILURE };
    }
    return {
      ok: true,
      createdSlotCount: typeof row.created_slot_count === "number" ? row.created_slot_count : 0,
      existingSlotCount: typeof row.existing_slot_count === "number" ? row.existing_slot_count : 0,
      totalSlotCount: row.total_slot_count,
      templateStatus: typeof row.template_status === "string" ? row.template_status : "published",
    };
  } catch {
    return { ok: false, reason: SAFE_PUBLISH_FAILURE };
  }
}

export const SAFE_COPY_FAILURE = "Couldn't copy last week — please try again.";

const KNOWN_COPY_FAILURES: ReadonlySet<string> = new Set([
  "Source and target weeks must be different.",
  "Schedule week copy window is too large.",
  "Caller is not authorized to manage schedules for this account.",
]);

export type CopyWeekOutcome =
  | {
      ok: true;
      copiedSlotCount: number;
      existingSlotCount: number;
      skippedSlotCount: number;
      totalSourceSlotCount: number;
    }
  | { ok: false; reason: string };

/** Copy last week's published slots into the target week (assignments never copy). Never throws. */
export async function copyShiftWeek(
  client: SupabaseClient,
  accountId: string,
  sourceWeekStart: string,
  targetWeekStart: string,
): Promise<CopyWeekOutcome> {
  try {
    const { data, error } = await client.rpc("copy_shift_week", {
      p_account_id: accountId,
      p_source_week_start: sourceWeekStart,
      p_target_week_start: targetWeekStart,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return { ok: false, reason: KNOWN_COPY_FAILURES.has(message) ? message : SAFE_COPY_FAILURE };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    if (row === undefined || typeof row.total_source_slot_count !== "number") {
      return { ok: false, reason: SAFE_COPY_FAILURE };
    }
    return {
      ok: true,
      copiedSlotCount: typeof row.copied_slot_count === "number" ? row.copied_slot_count : 0,
      existingSlotCount: typeof row.existing_slot_count === "number" ? row.existing_slot_count : 0,
      skippedSlotCount: typeof row.skipped_slot_count === "number" ? row.skipped_slot_count : 0,
      totalSourceSlotCount: row.total_source_slot_count,
    };
  } catch {
    return { ok: false, reason: SAFE_COPY_FAILURE };
  }
}

export const SAFE_CANCEL_FAILURE = "Couldn't cancel that shift slot — please try again.";

const KNOWN_CANCEL_FAILURES: ReadonlySet<string> = new Set([
  "Caller is not authorized to manage schedules for this account.",
  "Shift slot was not found for this account.",
  "Shift slot is already cancelled.",
  "Only published shift slots can be cancelled.",
  "Shift slot has active or historical assignment work and cannot be cancelled.",
]);

export type CancelOutcome = { ok: true; cancelledAssignmentCount: number } | { ok: false; reason: string };

/** Cancel a published slot (scheduled assignments move to cancelled). Never throws. */
export async function cancelShiftSlot(
  client: SupabaseClient,
  accountId: string,
  shiftSlotId: string,
): Promise<CancelOutcome> {
  try {
    const { data, error } = await client.rpc("cancel_shift_slot", {
      p_account_id: accountId,
      p_shift_slot_id: shiftSlotId,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return { ok: false, reason: KNOWN_CANCEL_FAILURES.has(message) ? message : SAFE_CANCEL_FAILURE };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    if (row === undefined || row.slot_status !== "cancelled") {
      return { ok: false, reason: SAFE_CANCEL_FAILURE };
    }
    return {
      ok: true,
      cancelledAssignmentCount:
        typeof row.cancelled_assignment_count === "number" ? row.cancelled_assignment_count : 0,
    };
  } catch {
    return { ok: false, reason: SAFE_CANCEL_FAILURE };
  }
}

export const SAFE_TEMPLATE_WEEK_CANCEL_FAILURE =
  "Couldn't cancel that template's remaining shift slots — please try again.";

// Value-free sentences from cancel_shift_template_week and the private per-slot helper it
// delegates to. Exact matches are useful to an operator; every other server detail stays
// behind the single safe sentence so this surface cannot become an error oracle.
const KNOWN_TEMPLATE_WEEK_CANCEL_FAILURES: ReadonlySet<string> = new Set([
  "Caller is not authorized to manage schedules for this account.",
  "Shift template was not found for this account.",
  "Cancellation week must be exactly seven calendar days.",
  "Only published shift slots can be cancelled.",
  "Shift slot has active or historical assignment work and cannot be cancelled.",
]);

export type CancelTemplateWeekOutcome =
  | {
      ok: true;
      cancelledSlotCount: number;
      alreadyCancelledSlotCount: number;
      cancelledAssignmentCount: number;
    }
  | { ok: false; reason: string };

function isReceiptCount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

/** Cancel every non-cancelled slot for one template in one exact Builder week. Never throws. */
export async function cancelShiftTemplateWeek(
  client: SupabaseClient,
  accountId: string,
  shiftTemplateId: string,
  weekStart: string,
  weekEnd: string,
): Promise<CancelTemplateWeekOutcome> {
  try {
    const { data, error } = await client.rpc("cancel_shift_template_week", {
      p_account_id: accountId,
      p_shift_template_id: shiftTemplateId,
      p_week_start: weekStart,
      p_week_end: weekEnd,
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return {
        ok: false,
        reason: KNOWN_TEMPLATE_WEEK_CANCEL_FAILURES.has(message)
          ? message
          : SAFE_TEMPLATE_WEEK_CANCEL_FAILURE,
      };
    }
    const row = Array.isArray(data) ? (data[0] as Record<string, unknown> | undefined) : undefined;
    if (
      row === undefined ||
      row.account_id !== accountId ||
      row.shift_template_id !== shiftTemplateId ||
      row.week_start !== weekStart ||
      row.week_end !== weekEnd ||
      !isReceiptCount(row.cancelled_slot_count) ||
      !isReceiptCount(row.already_cancelled_slot_count) ||
      !isReceiptCount(row.cancelled_assignment_count)
    ) {
      return { ok: false, reason: SAFE_TEMPLATE_WEEK_CANCEL_FAILURE };
    }
    return {
      ok: true,
      cancelledSlotCount: row.cancelled_slot_count,
      alreadyCancelledSlotCount: row.already_cancelled_slot_count,
      cancelledAssignmentCount: row.cancelled_assignment_count,
    };
  } catch {
    return { ok: false, reason: SAFE_TEMPLATE_WEEK_CANCEL_FAILURE };
  }
}
